"use client"

import { useRef, useState } from "react"
import { ImageUp } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * File picker + drag-and-drop target shared by the browser-only ad tools.
 * Hands the File straight to `onFile`; nothing is uploaded.
 */
export function MediaDropzone({
  onFile,
  hasMedia,
  fileName,
  onClear,
  error,
}: {
  onFile: (file: File | undefined) => void
  hasMedia: boolean
  fileName?: string
  onClear: () => void
  error: string | null
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  return (
    <div>
      <div
        onDragOver={e => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault()
          setDragging(false)
          onFile(e.dataTransfer.files?.[0])
        }}
        className={cn(
          "flex flex-col items-center gap-3 rounded-xl border border-dashed border-white/20 bg-white/[0.03] px-4 py-5 text-center transition-colors",
          dragging && "border-orange-400 bg-orange-500/10",
        )}
      >
        <ImageUp className="size-5 text-white/50" aria-hidden />
        <p className="text-sm text-white/70">
          {hasMedia && fileName ? (
            <span className="break-all">{fileName}</span>
          ) : (
            "Drop an image or video here"
          )}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => inputRef.current?.click()}
          >
            {hasMedia ? "Replace file" : "Choose file"}
          </Button>
          {hasMedia ? (
            <Button type="button" size="sm" variant="ghost" onClick={onClear}>
              Remove
            </Button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          className="sr-only"
          onChange={e => {
            onFile(e.target.files?.[0])
            e.target.value = ""
          }}
        />
      </div>
      <p className="mt-2 text-xs text-white/40">
        Processed in your browser. The file is never uploaded.
      </p>
      {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
    </div>
  )
}
